import { useId, useState, type FormEvent } from 'react';
import { Layers } from 'lucide-react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { CoverPicker } from '../ui/CoverPicker';
import { createSection, updateSection, UNNAMED } from '../db/sections';
import { ImageDecodeError } from '../db/images';
import type { Container } from '../db/board';
import type { Section } from '../db/schema';
import '../ui/form.css';

/**
 * Create when `section` is absent, edit when it isn't — the same form either
 * way, like the room dialog it's modelled on.
 *
 * No icon picker. A room is a place and reads as one at a glance down a rail;
 * a section is a pile of things you already own, and its cover is either the
 * things themselves or a picture you chose.
 */
export function SectionDialog({
  container,
  section,
  onClose,
}: {
  container: Container;
  section?: Section;
  onClose: () => void;
}) {
  const nameId = useId();

  const [name, setName] = useState(section?.name ?? '');
  const [cover, setCover] = useState<Blob | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent) {
    event.preventDefault();

    setBusy(true);
    setError(undefined);
    try {
      // A name is optional here, unlike a room's. Grouping two things is the
      // gesture; naming the result is an afterthought you may never have.
      if (section === undefined) await createSection(container, { name, cover: cover ?? undefined });
      else await updateSection(section.id, { name, cover });
      onClose();
    } catch (thrown) {
      setError(
        thrown instanceof ImageDecodeError
          ? thrown.message
          : "That section couldn't be saved. Nothing was changed.",
      );
      setBusy(false);
    }
  }

  return (
    <Dialog title={section === undefined ? 'New Section' : 'Edit Section'} onClose={onClose}>
      <form className="dialog__form" onSubmit={(event) => void submit(event)}>
        <CoverPicker
          label="Cover"
          existingImageId={section?.coverImageId}
          cover={cover}
          placeholder={<Layers size={40} aria-hidden />}
          onPick={setCover}
          onClear={() => setCover(null)}
        />

        <div className="field">
          <label className="section-label" htmlFor={nameId}>
            Name
          </label>
          <input
            id={nameId}
            className="input"
            value={name}
            placeholder={UNNAMED}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        {error !== undefined && (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}

        <DialogActions>
          <Button variant="quiet" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {section === undefined ? 'Create Section' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
